import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, BarChart3, Sunrise } from "lucide-react";
import { Screen } from "@/components/Screen";
import { PageHeader } from "@/components/PageHeader";
import { ResultBadge, RESULT_META } from "@/components/ResultBadge";
import { BalanceBar } from "@/components/BalanceBar";
import { WeeklyStrip } from "@/components/WeeklyStrip";
import { PremiumTeaserCard } from "@/components/PremiumTeaserCard";
import { useI18n } from "@/i18n/I18nProvider";
import { getCheckInByDate } from "@/store/store";
import {
  getLast7Days,
  getWeeklySummary,
} from "@/lib/insights";
import { formatDate, todayLocalISO } from "@/lib/date";

export default function Dashboard() {
  const { t, language } = useI18n();
  const navigate = useNavigate();

  const dateISO = useMemo(() => todayLocalISO(), []);
  const todayEntry = useMemo(() => getCheckInByDate(dateISO), [dateISO]);
  const last7 = useMemo(() => getLast7Days(), []);
  const hasAnyHistory = last7.some((d) => d.entry);
  const summary = useMemo(() => getWeeklySummary(), []);

  return (
    <Screen testId="dashboard-screen">
      <PageHeader titleKey="dashboard.title" testId="dashboard-header" />

      {!todayEntry ? (
        <>
          <EmptyStateCard onCta={() => navigate("/today")} />
          {hasAnyHistory ? (
            <div className="mt-6">
              <SectionHeading textKey="dashboard.strip.heading" />
              <div className="mt-3">
                <WeeklyStrip days={last7} />
              </div>
            </div>
          ) : null}
        </>
      ) : (
        <>
          <TodayCard
            entry={todayEntry}
            dateISO={dateISO}
            language={language}
            onViewDetails={() => navigate("/result")}
          />

          <div className="mt-6">
            <SectionHeading textKey="dashboard.strip.heading" />
            <div className="mt-3">
              <WeeklyStrip days={last7} />
            </div>
          </div>

          <div className="mt-6">
            <SectionHeading textKey="dashboard.weeklySummary.heading" />
            {summary ? (
              <WeeklySummaryCard summary={summary} />
            ) : (
              <NotEnoughDataCard />
            )}
          </div>

          <div className="mt-4">
            <PremiumTeaserCard
              Icon={BarChart3}
              title={t("premium.dashboardTeaser.title")}
              description={t("premium.dashboardTeaser.subtitle")}
              variant="compact"
              testId="dashboard-premium-teaser"
            />
          </div>

          <div className="mt-8">
            <PrimaryLink
              onClick={() => navigate("/today")}
              text={t("dashboard.redoCheckin")}
              testId="dashboard-redo-cta"
            />
          </div>
        </>
      )}
    </Screen>
  );
}

/* ------------------------------------------------------------------ */
/* Sub-components                                                     */
/* ------------------------------------------------------------------ */

function EmptyStateCard({ onCta }) {
  const { t } = useI18n();
  return (
    <div
      className="dc-card relative overflow-hidden mt-6 p-6 flex flex-col items-center text-center"
      data-testid="dashboard-empty"
    >
      <div
        className="absolute -top-14 -right-14 w-40 h-40 rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(244,201,93,0.22) 0%, rgba(244,201,93,0) 70%)",
        }}
      />
      <div
        className="w-14 h-14 rounded-full flex items-center justify-center relative"
        style={{
          background:
            "radial-gradient(circle, rgba(244,201,93,0.18) 0%, rgba(244,201,93,0) 70%)",
        }}
      >
        <Sunrise size={26} className="text-[#F4C95D]" strokeWidth={1.6} />
      </div>
      <p className="mt-4 text-[15px] text-[#CBD5E1] leading-relaxed max-w-[340px]">
        {t("dashboard.emptyState.message")}
      </p>
      <button
        type="button"
        onClick={onCta}
        data-testid="dashboard-empty-cta"
        className="group mt-5 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-[14px] font-medium transition-all duration-300 shadow-[0_10px_30px_-15px_rgba(244,201,93,0.6)] hover:shadow-[0_14px_36px_-14px_rgba(244,201,93,0.8)] active:scale-[0.985]"
        style={{
          background: "linear-gradient(180deg, #F5D07A 0%, #F4C95D 100%)",
          color: "#0F172A",
          letterSpacing: "-0.005em",
        }}
      >
        {t("dashboard.emptyState.cta")}
        <ArrowRight
          size={16}
          className="transition-transform duration-300 group-hover:translate-x-0.5"
          strokeWidth={2}
        />
      </button>
    </div>
  );
}

function TodayCard({ entry, dateISO, language, onViewDetails }) {
  const { t } = useI18n();
  const meta = RESULT_META[entry.result] || RESULT_META.stay;
  return (
    <div
      className="dc-card relative overflow-hidden mt-6 p-5"
      data-testid="dashboard-today-card"
    >
      <div
        className="absolute -top-16 -right-16 w-44 h-44 rounded-full pointer-events-none"
        style={{
          background: `radial-gradient(circle, ${meta.color}22 0%, ${meta.color}00 70%)`,
        }}
      />
      <div className="flex items-center justify-between mb-3 relative">
        <p className="text-[11px] uppercase tracking-[0.16em] text-[#E8D8B8]/80">
          {t("dashboard.today")}
        </p>
        <span className="text-xs text-[#94A3B8] capitalize">
          {formatDate(dateISO, language)}
        </span>
      </div>
      <div className="flex items-center gap-4 relative">
        <ResultBadge result={entry.result} size={72} />
        <div className="flex-1 min-w-0">
          <h2
            className="text-xl font-medium leading-tight"
            style={{ letterSpacing: "-0.015em", color: "#F8FAFC" }}
            data-testid="dashboard-today-title"
          >
            {t(`result.labels.${entry.result}.title`)}
          </h2>
          <p className="mt-1 text-[13px] text-[#CBD5E1] leading-snug line-clamp-2">
            {t(`result.labels.${entry.result}.description`)}
          </p>
        </div>
      </div>

      <div className="mt-5">
        <BalanceBar
          value={entry.balanceScore}
          result={entry.result}
          testId="dashboard-today-balance"
        />
      </div>

      <div className="mt-4 flex justify-end">
        <button
          type="button"
          onClick={onViewDetails}
          data-testid="dashboard-view-details"
          className="text-sm text-[#F4C95D]/85 hover:text-[#F4C95D] transition-colors duration-200 inline-flex items-center gap-1"
        >
          {t("dashboard.viewDetails")}
          <ArrowRight size={14} strokeWidth={1.7} />
        </button>
      </div>
    </div>
  );
}

function WeeklySummaryCard({ summary }) {
  const { t } = useI18n();
  const resultTitle = t(`result.labels.${summary.mostCommonResult}.title`);
  const phase = t(
    `dashboard.weeklySummary.phases.${summary.mostCommonResult}`,
  );
  const sentence = t("dashboard.weeklySummary.template", {
    result: resultTitle,
    phase,
  });
  return (
    <div
      className="dc-card mt-3 p-5"
      data-testid="dashboard-weekly-summary"
    >
      <p
        className="text-[15px] text-[#F8FAFC]/95 leading-relaxed"
        style={{ letterSpacing: "-0.005em" }}
        data-testid="dashboard-weekly-summary-text"
      >
        {sentence}
      </p>
      <p
        className="mt-3 text-xs text-[#94A3B8]"
        data-testid="dashboard-weekly-summary-average"
      >
        {t("dashboard.weeklySummary.averageBalance", {
          value: summary.averageBalance,
        })}
      </p>
    </div>
  );
}

function NotEnoughDataCard() {
  const { t } = useI18n();
  return (
    <div
      className="dc-card mt-3 p-5"
      data-testid="dashboard-weekly-not-enough"
    >
      <p className="text-[14px] text-[#CBD5E1] leading-relaxed">
        {t("dashboard.weeklySummary.notEnough")}
      </p>
    </div>
  );
}

function SectionHeading({ textKey }) {
  const { t } = useI18n();
  return (
    <h2
      className="text-[11px] uppercase tracking-[0.18em] text-[#E8D8B8]/80"
      style={{ letterSpacing: "0.18em" }}
    >
      {t(textKey)}
    </h2>
  );
}

function PrimaryLink({ onClick, text, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="group w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-[14px] font-medium transition-all duration-300 shadow-[0_10px_30px_-15px_rgba(244,201,93,0.6)] hover:shadow-[0_14px_36px_-14px_rgba(244,201,93,0.8)] active:scale-[0.985]"
      style={{
        background: "linear-gradient(180deg, #F5D07A 0%, #F4C95D 100%)",
        color: "#0F172A",
        letterSpacing: "-0.005em",
      }}
    >
      {text}
      <ArrowRight
        size={16}
        className="transition-transform duration-300 group-hover:translate-x-0.5"
        strokeWidth={2}
      />
    </button>
  );
}
